import { useMutation, useQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import { graphql } from '@/gql';
import type {
  ScraperCrawlHealth,
  ScraperCrawlTargetsQuery,
  UpdateScraperCrawlTargetMutation,
} from '@/gql/graphql';
import { saveErrorMessage } from '@/shared/lib/graphql-errors';

export const SCRAPER_OVERVIEW = graphql(`
  query ScraperOverview {
    scraperOverview {
      totalTargets
      activeTargets
      disabled
      successful
      failed
      uncrawled
      withDetections
      totalTools
      proposedTools
      ignoredTools
      lastCrawledAt
    }
  }
`);

export const SCRAPER_RELAY_HEALTH = graphql(`
  query ScraperRelayHealth {
    scraperOverview {
      relay {
        isConfigured
        isCrawlingEnabled
        checkIntervalSeconds
        latest {
          checkedAt
          isOk
          latencyMs
          error
        }
        lastOkAt
        downSince
        checksLastDay
        okLastDay
        recent {
          checkedAt
          isOk
          latencyMs
        }
      }
    }
  }
`);

export const SCRAPER_CRAWL_TARGETS = graphql(`
  query ScraperCrawlTargets($input: CrawlTargetsInput) {
    scraperCrawlTargets(input: $input) {
      items {
        id
        name
        domain
        crawlHost
        notes
        isDisabled
        disabledAt
        disabledSource
        disabledReason
        consecutiveFailures
        lastSucceededAt
        health
        healthReason
        matchFailures
        lastMatchError
        latestCrawl {
          id
          crawledAt
          error
          errorCategory
          isRetriable
          hasMatch
        }
      }
      pageInfo {
        endCursor
        totalCount
        hasNextPage
      }
    }
  }
`);

export const SCRAPER_CRAWL_TARGET = graphql(`
  query ScraperCrawlTarget($id: UUID!) {
    scraperCrawlTarget(id: $id) {
      id
      name
      domain
      crawlHost
      notes
      isDisabled
      disabledAt
      disabledSource
      disabledReason
      consecutiveFailures
      lastSucceededAt
      health
      healthReason
      matchFailures
      lastMatchError
      latestCrawl {
        id
        crawledAt
        error
        errorCategory
        isRetriable
        hasMatch
      }
    }
  }
`);

export const UPDATE_SCRAPER_CRAWL_TARGET = graphql(`
  mutation UpdateScraperCrawlTarget($input: UpdateScraperCrawlTargetInput!) {
    updateScraperCrawlTarget(input: $input) {
      id
      crawlHost
      notes
      isDisabled
      disabledAt
      disabledSource
      disabledReason
    }
  }
`);

export type ScraperCrawlTargetRow =
  ScraperCrawlTargetsQuery['scraperCrawlTargets']['items'][number];

export const useScraperOverview = () =>
  useQuery(SCRAPER_OVERVIEW, { fetchPolicy: 'cache-and-network' });

/** Polled, so an open overview page notices the relay going down or coming back. */
export const useScraperRelayHealth = () =>
  useQuery(SCRAPER_RELAY_HEALTH, { fetchPolicy: 'network-only', pollInterval: 60_000 });

/**
 * `network-only`, like the logo queue and for the same reason: this list is
 * *worked*. A row is disabled, noted or queued and the answer changes
 * immediately, so a cached page would show somebody the state they just left.
 */
export const useScraperCrawlTargets = (variables: {
  disabled?: boolean | null;
  uncrawled?: boolean | null;
  failed?: boolean | null;
  minConsecutiveFailures?: number | null;
  health?: ScraperCrawlHealth | null;
  matchFailed?: boolean | null;
  search?: string | null;
  after?: string | null;
  first: number;
}) =>
  useQuery(SCRAPER_CRAWL_TARGETS, {
    variables: { input: variables },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

export const useScraperCrawlTarget = (id: string) =>
  useQuery(SCRAPER_CRAWL_TARGET, { variables: { id }, fetchPolicy: 'network-only' });

type UpdatedCrawlTarget = UpdateScraperCrawlTargetMutation['updateScraperCrawlTarget'];

/** One target write, whichever of enable/disable, host and notes it carries; `describe` words the toast. */
export const useUpdateScraperCrawlTarget = (describe: (target: UpdatedCrawlTarget) => string) =>
  useMutation(UPDATE_SCRAPER_CRAWL_TARGET, {
    onCompleted: (data) => toast.success(describe(data.updateScraperCrawlTarget)),
    onError: (error) => toast.error(saveErrorMessage(error, "Couldn't save that brand.")),
  });
